import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Zap, ZapOff, Power, PowerOff, Loader2, Info } from 'lucide-react';

/**
 * Two independent kill-switches for external paid scraping APIs.
 * Zeroes out the runtime API keys server-side (env vars are preserved so you
 * can reactivate anytime). Backend endpoints:
 *   GET  /api/enrichment/apis-status
 *   POST /api/enrichment/serpapi   { enabled: bool }
 *   POST /api/enrichment/pappers   { enabled: bool }
 */
export function EnrichmentApisKillSwitchCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try { const { data } = await api.get('/enrichment/apis-status'); setStatus(data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (service, enabled) => {
    setBusy(service);
    try {
      await api.post(`/enrichment/${service}`, { enabled });
      toast.success(`${service === 'serpapi' ? 'SerpAPI' : 'Pappers'} ${enabled ? 'RÉACTIVÉ' : 'STOPPÉ'} — aucun nouvel appel ne partira`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setBusy('');
  };

  if (!status) return null;

  const rows = [
    {
      key: 'serpapi',
      label: 'SerpAPI',
      description: 'Recherche Google enrichie (téléphones, dirigeants, emails pro). 21 points d\'appel dans l\'app.',
      enabled: status.serpapi_enabled,
      configured: status.serpapi_key_configured,
    },
    {
      key: 'pappers',
      label: 'Pappers',
      description: 'Données légales (SIREN, dirigeants officiels, CA). 13 points d\'appel dans l\'app.',
      enabled: status.pappers_enabled,
      configured: status.pappers_key_configured,
    },
  ];

  return (
    <Card className="border-[#E5E7EB]" data-testid="enrichment-killswitch-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <Power className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-base font-bold text-[#18181B]">Kill-switch APIs externes payantes</h3>
            <p className="text-sm text-[#71717A] mt-1">
              Coupez les appels aux services payants sans supprimer les clés. Vos abonnements
              restent actifs (donc récupérables), mais aucune requête ne sera envoyée jusqu'à
              réactivation.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.key} className="flex items-center justify-between gap-4 p-3 rounded-lg border border-[#E5E7EB] bg-white"
              data-testid={`killswitch-row-${r.key}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#18181B]">{r.label}</span>
                  {r.enabled ? (
                    <Badge className="bg-[#DCFCE7] text-[#15803D] border-0 text-[10px]" data-testid={`badge-${r.key}-on`}>
                      <Zap className="w-3 h-3 mr-0.5" /> ACTIF
                    </Badge>
                  ) : (
                    <Badge className="bg-[#FEE2E2] text-[#B91C1C] border-0 text-[10px]" data-testid={`badge-${r.key}-off`}>
                      <ZapOff className="w-3 h-3 mr-0.5" /> STOPPÉ
                    </Badge>
                  )}
                  {!r.configured && (
                    <Badge className="bg-[#F5F5F4] text-[#71717A] border-0 text-[10px]">
                      <Info className="w-3 h-3 mr-0.5" /> Clé non configurée
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-[#71717A] mt-1">{r.description}</p>
              </div>
              {r.enabled ? (
                <Button size="sm" onClick={() => toggle(r.key, false)} disabled={busy === r.key || !r.configured}
                  className="bg-[#B91C1C] hover:bg-[#991B1B] text-white h-9 whitespace-nowrap"
                  data-testid={`stop-${r.key}-btn`}>
                  {busy === r.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PowerOff className="w-4 h-4 mr-1.5" />Stop {r.label}</>}
                </Button>
              ) : (
                <Button size="sm" onClick={() => toggle(r.key, true)} disabled={busy === r.key || !r.configured}
                  variant="outline" className="border-[#15803D] text-[#15803D] hover:bg-[#F0FDF4] h-9 whitespace-nowrap"
                  data-testid={`start-${r.key}-btn`}>
                  {busy === r.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Power className="w-4 h-4 mr-1.5" />Réactiver {r.label}</>}
                </Button>
              )}
            </div>
          ))}
        </div>

        <p className="text-[11px] text-[#71717A] italic">
          💡 Vos abonnements annuels restent facturés jusqu'à la fin du contrat côté fournisseur.
          Le kill-switch ne fait qu'arrêter la consommation. Utile pour économiser du crédit
          si vous n'en avez plus besoin.
        </p>
      </CardContent>
    </Card>
  );
}
